import React, { useState, useEffect, useMemo } from 'react';
import AdminSidebar from '../../components/admin/AdminSidebar';
import NoticeToast from '../../components/common/NoticeToast';
import { apiUrl } from '../../config/api';
import { apiFetch } from '../../api/client';
import { useNotice } from '../../hooks/useNotice';
import './ProgrammesManagement.css';

const API_BASE = apiUrl('/api/admin/programmes');

export default function ProgrammesManagement() {
  const { notice, showNotice, clearNotice } = useNotice();
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    actif: true
  });

  useEffect(() => {
    loadProgrammes();
  }, []);

  const loadProgrammes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(API_BASE, { auth: true });
      if (!response.ok) throw new Error('Erreur lors du chargement');
      const data = await response.json();
      setProgrammes(Array.isArray(data) ? data : (data.programmes || []));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const url = editingId ? `${API_BASE}/${editingId}` : API_BASE;
      const method = editingId ? 'PUT' : 'POST';

      const response = await apiFetch(url, {
        method,
        auth: true,
        json: true,
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur lors de la sauvegarde');
      }

      await loadProgrammes();
      resetForm();
      showNotice(editingId ? 'Programme modifié avec succès.' : 'Programme ajouté avec succès.', 'success');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (programme) => {
    setFormData({
      nom: programme.nom,
      actif: programme.actif
    });
    setEditingId(programme.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce programme ?')) return;

    try {
      const response = await apiFetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        auth: true,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Erreur lors de la suppression');
      }
      await loadProgrammes();
      showNotice('Programme supprimé avec succès.', 'success');
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({ nom: '', actif: true });
    setEditingId(null);
    setShowForm(false);
  };

  const sortedProgrammes = useMemo(() => {
    return [...programmes].sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  }, [programmes]);

  const programmeKpis = useMemo(() => {
    const activeCount = programmes.filter((programme) => Boolean(programme.actif)).length;
    const inactiveCount = programmes.filter((programme) => !programme.actif).length;

    return [
      { label: 'Total programmes', value: programmes.length, helper: 'référentiel complet' },
      { label: 'Programmes actifs', value: activeCount, helper: 'proposés dans les formulaires' },
      { label: 'Programmes inactifs', value: inactiveCount, helper: 'masqués des formulaires' },
    ];
  }, [programmes]);

  if (loading) {
    return (
      <div className="admin-shell">
        <AdminSidebar />
        <div className="admin-content">
          <div className="programmes-management">
            <div className="loading-spinner">Chargement des programmes...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <div className="admin-content">
        <div className="programmes-management">
          <div className="programmes-header">
            <h2>Gestion des programmes</h2>
            <div className="programmes-header-info">
              <span>{programmes.length} programme(s)</span>
            </div>
            <div className="header-actions">
              <button
                className="btn-add"
                onClick={() => setShowForm(!showForm)}
              >
                {showForm ? '✕ Annuler' : '+ Ajouter'}
              </button>
            </div>
          </div>

          <div className="programmes-kpi-strip">
            {programmeKpis.map((kpi) => (
              <div key={kpi.label} className="programmes-kpi-card">
                <span className="programmes-kpi-label">{kpi.label}</span>
                <strong className="programmes-kpi-value">{kpi.value}</strong>
                <span className="programmes-kpi-helper">{kpi.helper}</span>
              </div>
            ))}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {showForm && (
            <form className="programme-form card" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom du programme *</label>
                  <input
                    type="text"
                    name="nom"
                    placeholder="Ex: Courtier en immobilier résidentiel"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="actif"
                      checked={formData.actif}
                      onChange={handleChange}
                    />
                    Programme actif
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingId ? '💾 Mettre à jour' : '➕ Ajouter'}
                </button>
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  ✕ Annuler
                </button>
              </div>
            </form>
          )}

          <div className="programmes-table-wrapper card">
            {programmes.length === 0 ? (
              <p className="no-data">Aucun programme pour le moment. Ajoutez-en un pour commencer.</p>
            ) : (
              <>
                <table className="programmes-table">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Statut</th>
                      <th>Créé le</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProgrammes.map(programme => (
                      <tr key={programme.id}>
                        <td className="nom">{programme.nom}</td>
                        <td className="statut">
                          <span className={`badge ${programme.actif ? 'active' : 'inactive'}`}>
                            {programme.actif ? '✓ Actif' : '✗ Inactif'}
                          </span>
                        </td>
                        <td className="created">
                          {programme.created_at ? new Date(programme.created_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td className="actions">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(programme)}
                            title="Modifier"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(programme.id)}
                            title="Supprimer"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
        </div>
        </div>
        <NoticeToast notice={notice} onClose={clearNotice} />
      </div>
    </div>
  );
}
